import * as R from 'ramda';
import { differenceInDays, getWeekOfMonth, isDate, isSameMonth, isThisHour, isThisMonth, isThisWeek, isThisYear, isValid, format, endOfDay, endOfHour, endOfMonth, endOfWeek, endOfYear, isEqual, startOfDay, startOfHour, startOfMonth, startOfWeek, startOfYear, } from 'date-fns';
import { addDays, addHours, addMonths, addWeeks, addYears, subDays, subHours, subMonths, subWeeks, subYears, } from 'date-fns/fp';
import { is, any, map, range } from 'ramda';
import { tz, TZDate } from '@date-fns/tz';
import { flow } from 'fp-ts/lib/function.js';
import { isUsableVenueTimezone } from './venue-clock.js';
export const ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ss.sssZ';
// 2021-01-01T00:00:00.800Z
export const fullIsoDateRegex = /^[0-9]{4}-[10][0-9]-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]{3}Z$/;
export const isFullIsoDate = (date) => date.match(fullIsoDateRegex);
export const monthDisplay = (date, type) => format(date, type === 'full' ? 'LLLL' : type === 'condensed' ? 'LLL' : 'LLLLL');
export const getMonthsOfYear = (type) => range(0, 12).map((month) => monthDisplay(new Date(2020, month, 1), type));
export const getCurrentMonth = (type, currentDate = new Date()) => monthDisplay(currentDate, type);
// prettier-ignore
export const dayAbbreviations = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const mobileDayAbbreviations = [
    'Su',
    'Mo',
    'Tu',
    'We',
    'Th',
    'Fr',
    'Sa',
];
export const FULL_MONTHS_IN_YEAR = getMonthsOfYear('full');
// Week Helpers
export const startOfPreviousDay = flow(subDays(1), startOfDay);
export const startOfNextDay = flow(addDays(1), startOfDay);
export const endOfPreviousDay = flow(subDays(1), endOfDay);
export const endOfNextDay = flow(addDays(1), endOfDay);
export const thisDayInterval = (date) => ({
    start: startOfDay(date),
    end: endOfDay(date),
});
export const nextDayInterval = (date) => ({
    start: startOfNextDay(date),
    end: endOfNextDay(date),
});
export const previousDayInterval = (date) => ({
    start: startOfPreviousDay(date),
    end: endOfPreviousDay(date),
});
// Week Helpers
export const startOfPreviousWeek = flow(subWeeks(1), startOfWeek);
export const startOfNextWeek = flow(addWeeks(1), startOfWeek);
export const endOfPreviousWeek = flow(subWeeks(1), endOfWeek);
export const endOfNextWeek = flow(addWeeks(1), endOfWeek);
export const thisWeekInterval = (date) => ({
    start: startOfWeek(date),
    end: endOfWeek(date),
});
export const nextWeekInterval = (date) => ({
    start: startOfNextWeek(date),
    end: endOfNextWeek(date),
});
export const previousWeekInterval = (date) => ({
    start: startOfPreviousWeek(date),
    end: endOfPreviousWeek(date),
});
export const anyThisWeek = any(isThisWeek);
export const anyNextWeek = flow(map(addWeeks(1)), anyThisWeek);
export const anyPreviousWeek = flow(map(subWeeks(1)), anyThisWeek);
// Month Helpers
export const startOfPreviousMonth = flow(subMonths(1), startOfMonth);
export const startOfNextMonth = flow(addMonths(1), startOfMonth);
export const endOfPreviousMonth = flow(subMonths(1), endOfMonth);
export const endOfNextMonth = flow(addMonths(1), endOfMonth);
export const thisMonthInterval = (date) => ({
    start: startOfMonth(date),
    end: endOfMonth(date),
});
export const nextMonthInterval = (date) => ({
    start: startOfNextMonth(date),
    end: endOfNextMonth(date),
});
export const previousMonthInterval = (date) => ({
    start: startOfPreviousMonth(date),
    end: endOfPreviousMonth(date),
});
export const anyThisMonth = any(isThisMonth);
export const anyNextMonth = flow(map(addMonths(1)), anyThisMonth);
export const anyPreviousMonth = flow(map(subMonths(1)), anyThisMonth);
// Year Helpers
export const startOfPreviousYear = flow(subYears(1), startOfYear);
export const startOfNextYear = flow(addYears(1), startOfYear);
export const endOfPreviousYear = flow(subYears(1), endOfYear);
export const endOfNextYear = flow(addYears(1), endOfYear);
export const thisYearInterval = (date) => ({
    start: startOfYear(date),
    end: endOfYear(date),
});
export const nextYearInterval = (date) => ({
    start: startOfNextYear(date),
    end: endOfNextYear(date),
});
export const previousYearInterval = (date) => ({
    start: startOfPreviousYear(date),
    end: endOfPreviousYear(date),
});
export const anyThisYear = any(isThisYear);
export const anyNextYear = flow(map(addYears(1)), anyThisYear);
export const anyPreviousYear = flow(map(subYears(1)), anyThisYear);
// Hour Helpers
export const startOfPreviousHour = flow(subHours(1), startOfHour);
export const startOfNextHour = flow(addHours(1), startOfHour);
export const endOfPreviousHour = flow(subHours(1), endOfHour);
export const endOfNextHour = flow(addHours(1), endOfHour);
export const thisHourInterval = (date) => ({
    start: startOfHour(date),
    end: endOfHour(date),
});
export const nextHourInterval = (date) => ({
    start: startOfNextHour(date),
    end: endOfNextHour(date),
});
export const previousHourInterval = (date) => ({
    start: startOfPreviousHour(date),
    end: endOfPreviousHour(date),
});
export const anyThisHour = any(isThisHour);
export const anyNextHour = flow(map(addHours(1)), anyThisHour);
export const anyPreviousHour = flow(map(subHours(1)), anyThisHour);
export const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21)
        return 'th'; // covers 11th to 19th
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};
export const displayOrdinalNumber = (n) => `${n}${getOrdinalSuffix(n)}`;
export const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const formatTimezone = (timezone, format, options) => {
    const referenceDate = new Date(options?.referenceDate ?? Date.now());
    const timezoneContext = typeof timezone === 'string'
        ? new TZDate(referenceDate, timezone)
        : timezone.in(referenceDate);
    if (options?.blankStringIfMatchesUserTimezone &&
        timezoneContext.timeZone === userTimezone)
        return '';
    return [
        options?.wrapWithParentheses ? '(' : '',
        timezoneContext
            .toLocaleDateString(undefined, {
            day: '2-digit',
            // timeZoneName: 'short',
            timeZoneName: format,
        })
            .substring(4),
        options?.wrapWithParentheses ? ')' : '',
    ].join('');
};
const isTZDate = (date) => isDate(date) && 'timeZone' in date;
export function formatDate(strFormat, date, timezone, hideTimezone) {
    if (!date)
        return 'unknown date';
    const intrinsicTimezone = isTZDate(date) && date.timeZone ? { in: tz(date.timeZone) } : undefined;
    const options = getDateOptionsFromTimezone(timezone) ?? intrinsicTimezone;
    const d = new Date(date);
    if (strFormat === 'seconds')
        return format(d, 'T', options);
    if (strFormat === 'month')
        return format(d, 'LLLL', options);
    if (strFormat === 'month-year')
        return format(d, 'LLLL yyyy', options);
    if (strFormat === 'abbreviated-month')
        return format(d, 'LLL', options);
    if (strFormat === 'day')
        return format(d, 'EEEE', options);
    if (strFormat === 'abbreviated-day')
        return format(d, 'E', options);
    if (strFormat === 'day-first-letter')
        return format(d, 'EEEEE', options);
    if (strFormat === 'day-of-year')
        return format(d, 'D', options);
    if (strFormat === 'day-of-month')
        return format(d, 'd', options);
    if (strFormat === 'week-of-year')
        return format(d, 'l', options);
    if (strFormat === 'form')
        return format(d, 'yyyy-MM-dd', options);
    if (strFormat === 'condensed-readable')
        return format(d, 'E, LLL d', options);
    if (strFormat === 'numeric-month-day')
        return format(d, 'M/d', options);
    if (strFormat === 'week-of-month' || strFormat === 'month-week') {
        const week = getWeekOfMonth(d, options);
        if (strFormat === 'week-of-month')
            return week.toString();
        return `${format(d, 'MMM', options)}, Week #${week}`;
    }
    if (strFormat === 'month-day') {
        const month = format(d, 'LLLL', options);
        const dayOfMonth = format(d, 'd', options);
        return `${month} ${dayOfMonth}${getOrdinalSuffix(Number(dayOfMonth))}`;
    }
    // The (CDT)-style suffix is Intl-heavy; only time formats pay for it
    const resolvedTimezone = options?.in(new Date()).timeZone;
    const timezoneString = resolvedTimezone && !hideTimezone
        ? formatTimezone(resolvedTimezone, 'short', {
            blankStringIfMatchesUserTimezone: true,
            wrapWithParentheses: true,
            // abbrev must reflect the event's DST, not now's
            referenceDate: d,
        })
        : '';
    const time = [format(d, 'p', options), timezoneString].join(' ');
    const condensedTime = time.replace(':00', '').replace(' ', '').trim();
    if (strFormat === 'time')
        return time;
    if (strFormat === 'day-time')
        return `${format(d, 'EEEE', options)}, ${time}`;
    if (strFormat === 'abbreviated-time')
        return condensedTime.replace(/(AM|PM)/, '');
    if (strFormat === 'condensed-time')
        return condensedTime.toLowerCase();
    if (strFormat === 'condensed-readable-date-and-time')
        return [format(d, 'E, LLL d, h:mmaaa', options), timezoneString].join(' ');
    if (strFormat === 'readable-date-and-time')
        return [
            format(d, 'E, LLLL d, yyyy', options),
            'at',
            format(d, 'h:mmaaa', options),
            timezoneString,
        ].join(' ');
    return format(d, 'PPPP', options).slice(0, -6);
}
export function formatDateC(format, timezone) {
    return (date) => formatDate(format, date, timezone);
}
export function formatDateRange([firstFormat, secondFormat], [firstDate, secondDate], timezone, hideTimezone) {
    if (!firstDate || !secondDate)
        return 'unknown date';
    const start = parseDateOrThrow(firstDate);
    const end = parseDateOrThrow(secondDate);
    return `${formatDate(firstFormat, start, timezone, true)} - ${formatDate(secondFormat || firstFormat, end, timezone, hideTimezone)}`;
}
export const removeTimeFromDate = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};
export const getLocalTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
export const parseDate = (date) => {
    const validOrUndefined = (date) => (isValid(date) ? date : undefined);
    if (isDate(date))
        return validOrUndefined(date);
    if (is(String, date))
        return validOrUndefined(new Date(date));
    if (is(Number, date))
        return validOrUndefined(new Date(date));
    return undefined;
};
export const parseDateOrThrow = (date, error) => {
    if (isDate(date))
        return date;
    // if (!isValid(date)) throw new Error(error || 'Not a valid date')
    if (is(String, date))
        return new Date(date);
    if (is(Number, date))
        return new Date(date);
    throw new Error(error || 'The date received is an invalid type');
};
export const anyDateEquals = (dates) => (date) => dates.some((d) => isEqual(d, date));
/**
 * Recursively converts all dates in an object or array or data to ISO strings
 * Returns the input object unchanged if it is not an object, array, or date
 * @param input T: any
 * @returns T
 */
export const stringifyDatesDeep = R.cond([
    [R.is(Date), (date) => date.toISOString()],
    [R.is(Array), (input) => R.map(stringifyDatesDeep, input)],
    [R.is(Object), (input) => R.mapObjIndexed(stringifyDatesDeep, input)],
    [
        R.T,
        (input) => input, // Default case: return input unchanged
    ],
]);
/**
 *  Return the end of the month, plus 1 month if the passed date is
 * within 10 days of the end of the month
 *
 * @param currentDate Anchor for "now" — defaulted to `new Date()`; pass explicitly to keep this pure for tests.
 */
export const getEndOfMonthPlusMargin = (fromDate, currentDate = new Date()) => {
    const lastDayOfMonth = endOfMonth(fromDate);
    // If we aren't looking at the current month, just return one month of classes
    if (!isSameMonth(fromDate, currentDate))
        return lastDayOfMonth;
    const daysUntiLEnd = differenceInDays(lastDayOfMonth, currentDate);
    // A <10-day remainder would render a nearly-empty class list
    if (daysUntiLEnd < 10)
        return addMonths(1)(endOfDay(currentDate));
    // otherwise, just show the classes for this month
    return lastDayOfMonth;
};
const potentialDateKeys = [
    'startTime',
    'endTime',
    'start',
    'end',
    'startDate',
    'endDate',
];
export function convertDatesToTZDates(object, timezone, keysToConvert, _widenTypes) {
    const keys = keysToConvert ?? potentialDateKeys.filter((key) => key in object);
    return keys.reduce((acc, key) => {
        const value = acc[key];
        if (isDate(value)) {
            const converted = !timezone
                ? new TZDate(value, userTimezone)
                : typeof timezone === 'string'
                    ? new TZDate(value, timezone)
                    : timezone.in(value);
            return {
                ...acc,
                [key]: converted,
            };
        }
        return acc;
    }, object);
}
/** Blank/bad zone strings resolve to undefined — display never throws. */
export const getDateOptionsFromTimezone = (timezone) => typeof timezone === 'string'
    ? isUsableVenueTimezone(timezone)
        ? { in: tz(timezone) }
        : undefined
    : (timezone ?? undefined);
/** The clock an event renders on: venue zone when usable, viewer otherwise. */
export const displayTimezone = (event) => {
    const zone = event.venue?.timezone;
    return isUsableVenueTimezone(zone) ? zone : userTimezone;
};
//# sourceMappingURL=dates.js.map